import {
  getMetricLabelForDashboardMode,
  INSIGHTS_METRIC_OPTIONS,
  type InsightsMetricValue,
} from "constants/constants";

type MetricOption = (typeof INSIGHTS_METRIC_OPTIONS)[number];

export type MetricAggregationType = "sum" | "average" | "weighted_average";

export type MetricAggregationRule = {
  /**
   * Aggregation type to use for rendering a "total" row in tables.
   *
   * NOTE: Some metrics are mathematically ratios (e.g. ROAS) or rates (e.g. CTR)
   * and should generally be computed as a weighted average (requires a backend
   * denominator). Until denominators are available in the calculation response,
   * we fall back to `average` as a consistent baseline.
   */
  aggregationType: MetricAggregationType;
  /**
   * Allowed aggregation types for this metric. Useful for validation and for
   * future backend-provided overrides.
   */
  allowedAggregationTypes: readonly MetricAggregationType[];
};

// Pre-built Map at module level for O(1) lookups
// INSIGHTS_METRIC_OPTIONS is a static const, so this is safe to build once
const metricOptionsMap = new Map<string, MetricOption>(
  INSIGHTS_METRIC_OPTIONS.map((opt) => [opt.value, opt])
);

/**
 * Single source of truth for how table totals should be aggregated per metric.
 *
 * Sum metrics are additive across rows. Ratio/rate/cost metrics must NOT be summed.
 */
export const INSIGHTS_METRIC_TOTAL_AGGREGATION_RULES: Record<
  InsightsMetricValue,
  MetricAggregationRule
> = {
  customer_revenue: {
    aggregationType: "sum",
    allowedAggregationTypes: ["sum"],
  },
  total_spend: {
    aggregationType: "sum",
    allowedAggregationTypes: ["sum"],
  },
  customer_purchase: {
    aggregationType: "sum",
    allowedAggregationTypes: ["sum"],
  },
  impressions: {
    aggregationType: "sum",
    allowedAggregationTypes: ["sum"],
  },
  clicks: {
    aggregationType: "sum",
    allowedAggregationTypes: ["sum"],
  },
  // Ratios / rates / averages (do not sum)
  return_on_ad_spend: {
    aggregationType: "weighted_average",
    allowedAggregationTypes: ["average", "weighted_average"],
  },
  average_order_value: {
    aggregationType: "weighted_average",
    allowedAggregationTypes: ["average", "weighted_average"],
  },
  customer_acquisition_cost: {
    aggregationType: "average",
    allowedAggregationTypes: ["average", "weighted_average"],
  },
  cost_per_order: {
    aggregationType: "weighted_average",
    allowedAggregationTypes: ["average", "weighted_average"],
  },
  click_through_rate: {
    aggregationType: "weighted_average",
    allowedAggregationTypes: ["average", "weighted_average"],
  },
  cost_per_click: {
    aggregationType: "weighted_average",
    allowedAggregationTypes: ["average", "weighted_average"],
  },
  cost_per_mille: {
    aggregationType: "weighted_average",
    allowedAggregationTypes: ["average", "weighted_average"],
  },
  conversion_rate: {
    aggregationType: "weighted_average",
    allowedAggregationTypes: ["average", "weighted_average"],
  },
  new_customer_purchase_ratio: {
    aggregationType: "average",
    allowedAggregationTypes: ["average", "weighted_average"],
  },
};

/**
 * O(1) lookup for metric option by value.
 * Use this instead of INSIGHTS_METRIC_OPTIONS.find() for better performance.
 */
export function getMetricOption(
  metric: InsightsMetricValue | string
): MetricOption | undefined {
  return metricOptionsMap.get(metric);
}

export function getMetricTotalAggregationRule(
  metric: InsightsMetricValue | string
): MetricAggregationRule | undefined {
  return (
    INSIGHTS_METRIC_TOTAL_AGGREGATION_RULES as Record<
      string,
      MetricAggregationRule
    >
  )[metric];
}

export function getMetricTotalAggregationType(
  metric: InsightsMetricValue | string
): MetricAggregationType | undefined {
  return getMetricTotalAggregationRule(metric)?.aggregationType;
}

/**
 * Check if metric is a fixed/ad platform metric (CTR, CPC, CPM, Impressions, Clicks).
 * Fixed metrics come from ad platform data rather than customer data.
 */
export function isFixedMetric(metric: InsightsMetricValue | string): boolean {
  return getMetricOption(metric)?.isFixed ?? false;
}

/**
 * Get the display label for a metric.
 */
export function getMetricLabel(
  metric: InsightsMetricValue | string,
  dashboardMode?: string
): string {
  return getMetricLabelForDashboardMode(metric, dashboardMode);
}

/**
 * Get the metric type (currency, number, percentage).
 */
export function getMetricType(
  metric: InsightsMetricValue | string
): string | undefined {
  return getMetricOption(metric)?.type;
}

/**
 * Get the metric suffix (%, currency symbol, etc).
 */
export function getMetricSuffix(
  metric: InsightsMetricValue | string
): string | undefined {
  return getMetricOption(metric)?.suffix;
}
